/**
 * Enquête n°25 — Adapté de l'Étude de Cas CCS n°24
 * (Lois physiques appliquées aux 2 roues.)
 */

import { Lightbulb, ShieldCheck, AlertTriangle, Quote, Users } from 'lucide-react';

export const physiqueDeuxRouesEnquete = {
  slug: 'physique-deux-roues',
  themeCode: 'V',
  issueNumber: 25,
  totalIssues: 25,
  series: 'enquetes',
  category: 'Enquête physique appliquée',
  shortTitle: 'Physique du 2 roues',
  title: 'Pourquoi la moto ne tombe pas : 4 lois physiques qui te sauvent (ou pas)',
  kicker: 'Enquête n°25 · Série « Témoignages & Sécurité »',
  subtitle:
    "Effet gyroscopique, contrebraquage, transfert de masse, force centrifuge. 4 phénomènes invisibles qui décident si tu passes le virage ou si tu finis dans le fossé. Les comprendre = piloter mieux.",
  heroImage: 'https://images.unsplash.com/photo-1608501622839-8a8955239276?w=1600&q=80&auto=format&fit=crop',
  heroImageAlt: 'Motard en blouson noir au coucher du soleil — physique du 2 roues et dynamique de conduite',
  readTimeMin: 7,
  seoDescription:
    'Enquête sur les lois physiques appliquées à la moto : effet gyroscopique, contrebraquage, transfert de masse, force centrifuge. Comprendre pour piloter en sécurité.',
  seoKeywords:
    'physique moto, effet gyroscopique, contrebraquage, transfert de masse, force centrifuge, lois physiques 2 roues, inclinaison',

  edito: {
    title: 'La physique, ton meilleur copilote',
    body: 'Un automobiliste qui ignore la physique de sa voiture peut s’en sortir : 4 roues, 1 tonne, ESP, ABS. Un motard qui ignore la physique de sa moto, lui, finit dans le décor. <strong>4 phénomènes</strong> gouvernent ton 2 roues, et la plupart sont contre-intuitifs. Quand tu sauras pourquoi ta moto se penche à droite quand tu pousses sur la poignée droite, tu sauras la piloter.',
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: '1. L’effet gyroscopique — pourquoi ta moto reste droite',
      paragraphs: [
        '<strong>Principe</strong> : une roue qui tourne sur son axe se comporte comme un gyroscope. Plus elle tourne vite, plus elle résiste à toute force voulant la faire basculer.',
        '<strong>Application</strong> : à partir de <strong>20-30 km/h</strong>, ta moto se stabilise toute seule. Tu peux même lâcher le guidon (ne le fais pas en circulation).',
        "<strong>Conséquence</strong> : c'est aux allures lentes (< 20 km/h) que tu risques de tomber. Plus tu accélères, plus la moto est stable. Mais l’équilibre n’est plus une <strong>question d’équilibre</strong> au-delà de 30 km/h — c’est une question de <strong>physique gyroscopique</strong>.",
      ],
      keyPoints: [
        'Roue qui tourne = gyroscope',
        'Stabilité dès 20-30 km/h',
        'Plus vite = plus stable',
        'Allure lente = chute',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: '2. Le contrebraquage — le geste qui sauve',
      paragraphs: [
        '<strong>Contre-intuitif mais réel</strong> : au-dessus de 30 km/h, pour aller à droite, tu pousses sur la poignée DROITE (qui braque la roue à gauche pendant un instant). Cette force déstabilise le gyroscope, la moto se penche à droite, puis elle tourne à droite.',
        '<strong>Application terrain</strong> : si une voiture surgit à gauche et que tu dois esquiver à droite, ne tire pas le guidon vers la droite — POUSSE la poignée droite vers l’avant. Ton corps comprendra naturellement après quelques essais.',
        '<strong>Piège classique</strong> : en situation d’urgence, les motards débutants tirent le guidon dans le sens contraire, leur moto va donc à l’envers de leur intention. Résultat : chute ou collision frontale.',
      ],
      keyPoints: [
        'Pousser droite = tourner droite',
        'Au-dessus de 30 km/h seulement',
        'Contre-intuitif au début',
        'S’entraîner sur parking',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: '3. Le transfert de masse — accélérer ou freiner ?',
      paragraphs: [
        '<strong>Au freinage avant</strong> : la masse de la moto + pilote se transfère sur la roue avant (compression de la fourche). La roue avant fournit alors <strong>70 % du freinage</strong>, l’arrière 30 %.',
        '<strong>À l’accélération</strong> : c’est l’inverse, la masse se transfère vers l’arrière. La roue arrière fournit le grip nécessaire à la motricité.',
        '<strong>Règle d’or</strong> : on freine fort en ligne droite, on relâche progressivement à l’entrée du virage, on rouvre les gaz à la sortie. Freiner penché = transfert déséquilibré + perte d’adhérence = chute.',
      ],
      keyPoints: [
        'Freinage : 70 % avant / 30 % arrière',
        'Accélération : poids arrière',
        'Freiner penché = chute',
        'Rouvrir les gaz en sortie',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 4',
      title: '4. La force centrifuge — ennemie du virage',
      paragraphs: [
        '<strong>Principe</strong> : en virage, ta moto subit une force qui veut la pousser vers l’<strong>extérieur</strong> du virage. Pour la compenser, tu te penches vers l’<strong>intérieur</strong>.',
        '<strong>Formule simplifiée</strong> : la force centrifuge augmente au CARRÉ de la vitesse. Double la vitesse → 4 fois plus de force centrifuge → il faut se pencher 2 fois plus.',
        '<strong>Limite physique</strong> : au-delà de <strong>40-45°</strong> d’inclinaison sur route, les repose-pieds touchent le sol → la moto se redresse violemment → chute. C’est pour ça qu’on freine AVANT le virage et qu’on adapte sa vitesse à la courbure.',
      ],
      keyPoints: [
        'Centrifuge pousse à l’extérieur',
        'Augmente au CARRÉ de la vitesse',
        'Limite 40-45° sur route',
        'Adapter vitesse à courbure',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Une moto sans pilote ne tombe pas (à 30 km/h)',
      body: "En 2011, des chercheurs de l’Université de Delft (Pays-Bas) ont prouvé qu’une moto sans pilote, lancée à 30 km/h, est <strong>auto-stable</strong> jusqu’à environ 100 m. Effet gyroscopique + géométrie de direction (chasse + angle de colonne) créent un équilibre dynamique. Conclusion : c’est ton CORPS qui est le moins fiable, pas la moto. D’où l'importance de la posture en virage : reste relâché, laisse la moto faire.",
    },
    {
      type: 'tip',
      icon: Users,
      title: 'Tu veux expérimenter ?',
      body: 'Va sur un parking désert. Roule à 30 km/h, pousse légèrement sur la poignée droite. Sens ce qui se passe. Refais-le. Ce réflexe peut te sauver la vie en cas d’évitement d’urgence. La physique, c’est pas une option — c’est une formation continue.',
    }
  ],
};

export default physiqueDeuxRouesEnquete;
